import { useEffect } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../state/store";
import { setLocationsError, setLocationsLoading, setUsersRefresh } from "../../state/ducks";
import { selectUsersMarketFilter } from "../../state/selectors";

import { alignItems, marketNames, strCode } from "../../consts";
import { locationsService, log, notificationService, t, usersService } from "../../services";
import { withSection } from "../../hoc";
import { responseUtil } from "../../utils";
import {
  useFetchGroups,
  useFetchGroupAssignments,
  useFetchUsers,
  useSaveStore,
  useDeleteStore
} from "../../apiHooks";

import {
  FlexContainers,
  Heading5,
  PageContainer,
  RefreshButton,
  WrapperContainer
} from "../../components";
import { UsersTable } from "../../containers";

const UserManagementPage = () => {
  const {
    fetchCallback: fetchUsers,
    data: users,
    loading: usersLoading,
    refresh: usersRefresh
  } = useFetchUsers();

  const { mutationCallback: onSaveStore } = useSaveStore();
  const { mutationCallback: onDeleteStore } = useDeleteStore();

  const { fetchCallback: fetchGroups, refresh: groupsRefresh } = useFetchGroups();
  const { fetchCallback: fetchGroupAssignments } = useFetchGroupAssignments();
  const { apps, markets, groups } = useSelector((store) => store.users?.userFilters);

  const onImportUsersFile = async (file) => {
    try {
      const res = await usersService.importUsersFile(file);

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.IMPORT_USERS_SUCCESS),
          message: t(strCode.IMPORT_USERS_SUCCESS_INFO)
        });

        dispatch(setUsersRefresh(true));
      }
    } catch (err) {
      log.error("UserManagementPage: Failed to import users", err);
      notificationService.showError({
        title: t(strCode.IMPORT_USERS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      throw err;
    }
  };

  //Stores
  //___________________________________________________
  const fetchStores = async () => {
    dispatch(setLocationsLoading(true));
    dispatch(setLocationsError(null));

    try {
      const market = selectUsersMarketFilter()(getState());
      if (market !== marketNames.allMarkets) {
        await locationsService.fetchLocations(market);
      }
    } catch (err) {
      dispatch(setLocationsError(err));
      log.error("UserManagementPage: Failed to fetch stores", err);
      notificationService.showError({
        title: t(strCode.LOCATIONS_FETCH_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setLocationsLoading(false));
      dispatch(setLocationsError(false));
    }
  };

  const onMarketSelected = (market) => locationsService.fetchLocations(market);

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (usersRefresh || groupsRefresh) {
      const refreshUsers = async () => {
        const assignments = await fetchGroupAssignments();
        fetchGroups(true);
        fetchUsers(true, assignments);
      };

      refreshUsers();
    }
  }, [usersRefresh, groupsRefresh]);

  useDeepCompareEffectNoCheck(() => {
    if (markets || apps || groups) {
      dispatch(setUsersRefresh(true));
    }
  }, [markets, apps, groups]);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16}>
          <Heading5>{t(strCode.USER_MANAGEMENT_TITLE)}</Heading5>

          <RefreshButton
            isLoading={usersLoading}
            handleRefresh={() => dispatch(setUsersRefresh(true))}
          />
        </WrapperContainer>

        <UsersTable
          usersData={users ? users : []}
          onImportUsersFile={onImportUsersFile}
          onSaveStore={onSaveStore}
          onDeleteStore={onDeleteStore}
          onMarketSelected={onMarketSelected}
        />
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(UserManagementPage);
